import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HERO, BRAND } from "@/site.config";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <img
          src={HERO.background}
          alt=""
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-grid opacity-30 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_30%,#000_30%,transparent_75%)]" />
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#9DEB30]/10 blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 w-full">
        <div className="max-w-4xl">
          <div
            data-testid="hero-kicker"
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#9DEB30]/30 bg-[#9DEB30]/5 fade-up"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-[#9DEB30] pulse-glow" />
            <span className="text-xs font-mono uppercase tracking-[0.18em] text-[#9DEB30]">
              {HERO.kicker}
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-display mt-6 text-4xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tight fade-up"
            style={{ animationDelay: "120ms" }}
          >
            Sécurisez,{" "}
            <span className="text-[#9DEB30] neon-text">valorisez</span> et entretenez{" "}
            <span className="block text-zinc-400">vos infrastructures.</span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 text-base sm:text-lg max-w-2xl text-zinc-300 fade-up"
            style={{ animationDelay: "240ms" }}
          >
            {HERO.subtitle}
          </p>

          <div
            data-testid="hero-slogan"
            className="mt-8 flex items-center gap-3 fade-up"
            style={{ animationDelay: "300ms" }}
          >
            <span className="h-px w-8 bg-gradient-to-r from-transparent to-[#9DEB30]/60" />
            <span className="font-display text-lg sm:text-2xl font-bold tracking-tight">
              <span className="text-[#9DEB30] neon-text">Sécuriser</span>
              <span className="text-zinc-600 mx-2">·</span>
              <span className="text-[#9DEB30] neon-text">Valoriser</span>
              <span className="text-zinc-600 mx-2">·</span>
              <span className="text-[#9DEB30] neon-text">Innover</span>
            </span>
          </div>

          <div
            className="mt-10 flex flex-wrap items-center gap-4 fade-up"
            style={{ animationDelay: "360ms" }}
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full btn-neon text-sm sm:text-base"
            >
              {HERO.cta}
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full btn-ghost-neon text-sm sm:text-base"
            >
              Découvrir nos services
            </Link>
          </div>

          <div
            className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm fade-up"
            style={{ animationDelay: "420ms" }}
          >
            <span className="text-zinc-500">Déjà avec nous ?</span>
            <a
              href={BRAND.clientPortal}
              data-testid="hero-client-portal"
              className="font-medium text-[#9DEB30] hover:text-[#b6f55c] underline underline-offset-4 decoration-[#9DEB30]/40 hover:decoration-[#9DEB30] transition-colors"
            >
              Espace client
            </a>
            <span className="text-zinc-700">·</span>
            <a
              href={BRAND.teamPortal}
              data-testid="hero-team-portal"
              className="font-medium text-zinc-300 hover:text-white underline underline-offset-4 decoration-white/20 hover:decoration-white/60 transition-colors"
            >
              Accès équipe
            </a>
          </div>

          <div
            className="mt-16 grid grid-cols-2 gap-6 max-w-md fade-up"
            style={{ animationDelay: "480ms" }}
          >
            {[
              { k: "12 m", v: "Aspiration sans nacelle" },
              { k: "100%", v: "Couverture Martinique" },
            ].map((stat, i) => (
              <div
                key={i}
                className="border-l border-white/10 pl-4"
                data-testid={`hero-stat-${i}`}
              >
                <div className="font-display text-2xl sm:text-3xl font-extrabold text-white">
                  {stat.k}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{stat.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[#0A0A0A] pointer-events-none" />
    </section>
  );
}
